// Do not directly import from exhibition-finder (avoid circular imports)
import { EMPTY_OPTION_VALUE } from "../components/exhibition-finder/constants";
import {
  ExhibitionFinderFiltersFormTypes,
  ExhibitionTimeFilter,
} from "../components/exhibition-finder/types";
import { otherMaterial } from "../components/forms/forms-fields/materials-form-fields";
import { MaterialType } from "../types/materials";
import { getSearchParams, getUpdatedSearchParamsUrl } from "./search-params";

export function areFormFiltersEmpty(filters: Partial<ExhibitionFinderFiltersFormTypes>) {
  const { materials, location, search } = filters;
  const materialsAreEmpty = !materials || !Object.values(materials).some(Boolean);
  return materialsAreEmpty && !location && !search;
}

export function areFiltersEmpty(filters: Partial<ExhibitionFinderFiltersFormTypes>) {
  const { time } = filters;
  const timeIsEmpty = !time || time === ExhibitionTimeFilter.ALL;
  return areFormFiltersEmpty(filters) && timeIsEmpty;
}

/**
 * @param exhibitions
 * @param appliedFilters
 * @returns The list of exhibitions matching filters
 */
export function filterExhibitions(
  exhibitions: Queries.ExhibitionCardDataFragment[],
  appliedFilters: ExhibitionFinderFiltersFormTypes
) {
  const { materials, location, time, search } = appliedFilters;

  if (areFiltersEmpty(appliedFilters)) return exhibitions;

  return exhibitions.filter((exhibition) => {
    // Materials match
    if (
      Object.values(materials).some(Boolean) &&
      !exhibition.materials?.some((material) => materials[material as MaterialType])
    )
      return false;
    // location matches
    if (location && exhibition.location !== location) return false;
    if (time) {
      if (!exhibition.startDate || !exhibition.endDate) return false;
      const isPast = new Date(exhibition.endDate).getTime() < new Date().getTime();
      const isFuture = new Date(exhibition.endDate).getTime() > new Date().getTime();

      if (time === ExhibitionTimeFilter.PAST && !isPast) return false;
      if (time === ExhibitionTimeFilter.FUTURE_OR_ONGOING && !isFuture) return false;
    }
    if (search && !exhibition.title?.match(new RegExp(search, "gi"))) return false;
    return true;
  });
}

export function getDefaultExhibitionFinderFilters() {
  const defaultFiltersValues: ExhibitionFinderFiltersFormTypes = {
    materials: {
      [MaterialType.Wood]: false,
      [MaterialType.Stone]: false,
      [MaterialType.Materia]: false,
      [MaterialType.Glass]: false,
      [MaterialType.Metal]: false,
      [otherMaterial]: false,
    },
    time: ExhibitionTimeFilter.ALL,
    search: "",
    location: EMPTY_OPTION_VALUE,
  };
  return defaultFiltersValues;
}

export function getFiltersFromSearchParams(
  searchParamKey: string
): null | Partial<ExhibitionFinderFiltersFormTypes> {
  const searchParams = getSearchParams();
  const searchParamFiltersAsString = searchParams?.get(searchParamKey || "filter");
  if (!searchParamFiltersAsString) return null;
  return JSON.parse(searchParamFiltersAsString);
}

export function setFiltersInSearchParams(
  _filter: null | Partial<ExhibitionFinderFiltersFormTypes>,
  searchParamKey: string
) {
  const filter = { ..._filter };
  if (filter.materials && !Object.values(filter.materials).some(Boolean)) {
    delete filter.materials;
  }
  if (!filter.location) {
    delete filter.location;
  }
  if (!filter.time || filter.time === ExhibitionTimeFilter.ALL) {
    delete filter.time;
  }
  if (!filter.search) delete filter.search;
  if (typeof window !== "undefined") {
    const path = getUpdatedSearchParamsUrl({
      [searchParamKey || "filter"]:
        filter && Object.keys(filter).length ? JSON.stringify(filter) : null,
    });
    window.history.pushState({ path }, "", path);
  }
}
